import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { consentAPI, settingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ConsentPage = () => {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const submitConsent = async () => {
    if (!checked) return;
    setSubmitting(true);
    setError('');
    try {
      await consentAPI.giveConsent();
      // Mark consent acknowledged for this login session so the guard lets the
      // participant through until their next login (which clears this flag).
      try { sessionStorage.setItem('consent_ack', 'true'); } catch (_) {}
      await refreshUser();
      navigate('/chat');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to submit consent');
    } finally {
      setSubmitting(false);
    }
  };

  React.useEffect(() => {
    settingsAPI.get().then(res => setSettings(res.data?.settings || {})).catch(() => {});
  }, []);

  return (
    <div style={{
      ...styles.container,
      padding: isMobile ? 12 : 20
    }}>
      <div style={{
        ...styles.card,
        padding: isMobile ? 20 : 32,
        borderRadius: isMobile ? 12 : 16,
        maxWidth: isMobile ? '100%' : 720,
        margin: isMobile ? '0 8px' : 0
      }}>
        <h2 style={{
          ...styles.title,
          fontSize: isMobile ? 22 : 28
        }}>{settings['consent.title'] || 'Consent to Participate'}</h2>
        <p style={{
          ...styles.subtitle,
          fontSize: isMobile ? 14 : 16
        }}>{settings['consent.subtitle'] || 'Research Chat Application'}</p>
        <div style={{
          ...styles.content,
          fontSize: isMobile ? 14 : 16
        }}>
          {(settings['consent.body'] || '').split('\n').filter(Boolean).map((line, idx) => (
            <p key={idx}>{line}</p>
          ))}
          {settings['consent.link_url'] && (
            <p style={{ marginTop: 12 }}>
              <a
                href={settings['consent.link_url']}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#059669', fontWeight: 600, textDecoration: 'underline' }}
              >
                {settings['consent.link_text'] || 'Read the full participant information (opens in a new tab)'}
              </a>
            </p>
          )}
        </div>
        {error && <div style={styles.error}>{error}</div>}
        <label style={{
          ...styles.checkboxRow,
          fontSize: isMobile ? 14 : 16,
          flexWrap: 'wrap'
        }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{ marginRight: 8, width: 20, height: 20 }}
          />
          {settings['consent.checkbox'] || 'I understand and consent to my chat being collected for research.'}
        </label>
        <button
          onClick={submitConsent}
          disabled={!checked || submitting}
          style={{
            ...styles.button,
            ...((!checked || submitting) ? styles.buttonDisabled : {}),
            padding: isMobile ? '12px 20px' : '10px 16px',
            fontSize: isMobile ? 15 : 14,
            width: isMobile ? '100%' : 'auto'
          }}
        >
          {submitting ? 'Submitting…' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 35%, #34d399 100%)',
    padding: 20,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 32,
    width: '100%',
    maxWidth: 720,
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
  },
  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 700,
    color: '#111827'
  },
  subtitle: {
    marginTop: 6,
    color: '#6b7280'
  },
  content: {
    marginTop: 16,
    color: '#374151',
    lineHeight: 1.6
  },
  checkboxRow: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center'
  },
  button: {
    marginTop: 16,
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    padding: '10px 16px',
    borderRadius: 8,
    cursor: 'pointer',
    fontWeight: 600
  },
  buttonDisabled: {
    backgroundColor: '#9ca3af',
    cursor: 'not-allowed'
  },
  error: {
    marginTop: 12,
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    padding: 10,
    borderRadius: 8
  }
};

export default ConsentPage;


